import { roomService } from "./roomService";
import { guestService } from "./guest-service";
import { hotelService } from "./hotel-service";
import { fileService } from "./file-service";
import { documentService } from "./document-service";
import { requestService } from "./requestService";
import { tasksService } from "./tasksService";
import { BaseService } from "./BaseService";
import { IRoomService } from "./interfaces/IRoomService";
import { IRequestService } from "./interfaces/IRequestService";
import { bookingCheckinSettingsService } from "./booking-checkin-settings.service";
import { bookingCheckinService } from "./booking-checkin.service";
import { bookingCheckoutSettingsService } from "./booking-checkout-settings.service";
import { bookingCheckoutService } from "./booking-checkout.service";

class ServiceRegistry {
  private services: Map<string, BaseService> = new Map();

  constructor() {
    // Register all services
    this.registerService("room", roomService);
    this.registerService("guest", guestService);
    this.registerService("hotel", hotelService);
    this.registerService("file", fileService);
    this.registerService("document", documentService);
    this.registerService("request", requestService);
    this.registerService("tasks", tasksService);
    this.registerService(
      "bookingCheckinSettings",
      bookingCheckinSettingsService,
    );
    this.registerService("bookingCheckin", bookingCheckinService);
    this.registerService("bookingCheckout", bookingCheckoutService);
    this.registerService(
      "bookingCheckoutSettings",
      bookingCheckoutSettingsService,
    );
  }

  registerService(name: string, service: BaseService): void {
    this.services.set(name, service);
  }

  getService<T extends BaseService>(name: string): T {
    const service = this.services.get(name);
    if (!service) {
      throw new Error(`Service '${name}' not found`);
    }
    return service as T;
  }

  setAuthToken(token: string): void {
    this.services.forEach((service) => {
      service.setAuthToken(token);
    });
  }

  clearAuthToken(): void {
    this.services.forEach((service) => {
      service.clearAuthToken();
    });
  }

  setGlobalHeaders(headers: Record<string, string>): void {
    this.services.forEach((service) => {
      service.setHeaders(headers);
    });
  }

  getServiceNames(): string[] {
    return Array.from(this.services.keys());
  }

  hasService(name: string): boolean {
    return this.services.has(name);
  }

  unregisterService(name: string): boolean {
    return this.services.delete(name);
  }

  getRoomService(): IRoomService {
    const service = this.services.get("room");
    if (!service) {
      throw new Error("Room service not found");
    }
    return service as IRoomService;
  }

  getRequestService(): IRequestService {
    const service = this.services.get("request");
    if (!service) {
      throw new Error("Request service not found");
    }
    return service as IRequestService;
  }
}

// Create and export the singleton instance
export const serviceRegistry = new ServiceRegistry();

export { roomService } from "./roomService";
export { guestService } from "./guest-service";
export { hotelService } from "./hotel-service";
export { fileService } from "./file-service";
export { documentService } from "./document-service";

export { requestService } from "./requestService";
export { tasksService } from "./tasksService";

export type {
  Room,
  CreateRoomRequest,
  UpdateRoomRequest,
  RoomFilters,
} from "@/types/room";
export type {
  Guest,
  GuestFilters,
  GuestsResponse,
  CreateGuestDto,
  UpdateGuestDto,
} from "@/types/guest";
export type {
  Document,
  CreateDocumentDto,
  CreateDocumentResponse,
} from "@/types/document";
export type { ApiError } from "./BaseService";
export { bookingCheckinSettingsService } from "./booking-checkin-settings.service";
export { bookingCheckinService } from "./booking-checkin.service";
export { bookingCheckoutService } from "./booking-checkout.service";
export { bookingCheckoutSettingsService } from "./booking-checkout-settings.service";
